import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import VoiceDictationInput from '../../components/VoiceDictationInput';
import SignatureInput from '../../components/SignatureInput';
import WoundHealingTracker from '../../components/WoundHealingTracker';
import { FileText, Save, ArrowLeft, Printer, Plus, Trash2, Camera, Image, X, Upload, ZoomIn } from 'lucide-react';
import { exportFillableFromDom } from '../../utils/fillable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Checkbox field component - defined outside to avoid unstable nested component
const CheckboxField = ({ label, checked, onChange, className = '' }) => (
  <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
    <input 
      type="checkbox" 
      checked={checked || false} 
      onChange={(e) => onChange(e.target.checked)} 
      className="w-4 h-4 rounded border-gray-300"
    />
    <span className="text-sm">{label}</span>
  </label>
);

const WoundCareFlowSheet = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('id');
  
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const woundImageInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    medicalRecordNumber: '',
    
    // Wound Images
    woundImages: [], // Array of {id, imageData, dateTaken, description}
    
    // Wound Etiology
    woundEtiologyTrauma: false,
    woundEtiologyDiabetic: false,
    woundEtiologyVenousStasis: false,
    woundEtiologyBurn: false,
    woundEtiologyPressure: false,
    woundEtiologySurgery: false,
    woundEtiologyOther: '',
    
    // Wound Number
    woundNumber: '',
    
    // Wound Type
    woundTypeTrauma: false,
    woundTypeTraumaCause: '',
    woundTypeBurn: false,
    woundTypePressureUlcer: false,
    woundTypePressureStageI: false,
    woundTypePressureStageII: false,
    woundTypePressureStageIII: false,
    woundTypePressureStageIV: false,
    woundTypeUnobservable: false,
    woundTypeDiabeticUlcer: false,
    woundTypeVenousStasisUlcer: false,
    woundTypeArterialUlcer: false,
    woundTypeOther: '',
    
    // Wound Description
    woundLocation: '',
    woundWidth: '',
    woundLength: '',
    woundDepth: '',
    hasTunneling: false,
    hasUndermining: false,
    woundUnobservable: false,
    unobservableDueToEschar: false,
    unobservableDueToCast: false,
    unobservableDueToNonRemovableDressing: false,
    nonRemovableDressingType: '',
    unobservableDueToOther: '',
    drainageAmountColor: '',
    woundColor: '',
    woundOdor: '',
    surroundingTissue: '',
    
    // Granulation Tissue
    fullyGranulating: false,
    earlyPartialGranulation: false,
    notHealing: false,
    
    // Current Treatment
    currentTreatment: '',
    
    // Signatures (comprehensive - typed, voice recording, handwritten)
    staffSignature: { typed: '', audioURL: null, canvasDataURL: null },
    staffSignatureDate: '',
    
    // Flow Sheet Entries (table)
    flowSheetEntries: [
      { date: '', woundLocation: '', measurements: '', drainageAmount: '', drainageColor: '', drainageOdor: '', comments: '' }
    ]
  });

  // Handle wound image upload
  const handleWoundImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImage = {
        id: Date.now().toString(),
        imageData: reader.result,
        dateTaken: new Date().toISOString().split('T')[0],
        description: ''
      };
      setFormData(prev => ({
        ...prev,
        woundImages: [...(prev.woundImages || []), newImage]
      }));
      toast.success('Wound image added');
      setUploading(false);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const removeWoundImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      woundImages: (prev.woundImages || []).filter(img => img.id !== imageId)
    }));
    toast.success('Image removed');
  };

  const updateWoundImage = (imageId, field, value) => {
    setFormData(prev => ({
      ...prev,
      woundImages: (prev.woundImages || []).map(img => 
        img.id === imageId ? {...img, [field]: value} : img
      )
    }));
  };

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
  }, [formId]);

  const getToken = () => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  };

  const fetchPatients = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setPatients(data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/skilled-nursing/wound-care/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.form) {
        setFormData(prev => ({ ...prev, ...data.form.formData }));
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load form');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFlowSheetChange = (index, field, value) => {
    const newEntries = [...formData.flowSheetEntries];
    newEntries[index][field] = value;
    setFormData(prev => ({ ...prev, flowSheetEntries: newEntries }));
  };

  const addFlowSheetEntry = () => {
    setFormData(prev => ({
      ...prev,
      flowSheetEntries: [...prev.flowSheetEntries, { date: '', woundLocation: '', measurements: '', drainageAmount: '', drainageColor: '', drainageOdor: '', comments: '' }]
    }));
  };

  const removeFlowSheetEntry = (index) => {
    if (formData.flowSheetEntries.length > 1) {
      const newEntries = formData.flowSheetEntries.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, flowSheetEntries: newEntries }));
    }
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const url = formId 
        ? `${API_URL}/api/skilled-nursing/wound-care/${formId}`
        : `${API_URL}/api/skilled-nursing/wound-care`;
      
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      
      const payload = {
        patientId: formData.patientId,
        patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : formData.patientName,
        formType: 'wound-care-flow-sheet',
        formData: formData
      };
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form?.id) {
          navigate(`/dashboard/skilled-nursing/wound-care?id=${data.form.id}`);
        }
      } else {
        toast.error('Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <FileText className="w-8 h-8 text-[#f39c12]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Wound Care Flow Sheet</h1>
                <p className="text-sm text-gray-500">Ivory Home Health Care, Inc.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => exportFillableFromDom(document.getElementById('main-content'), 'Wound_Care_Flow_Sheet.pdf')}>
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          {/* Company Header */}
          <div className="text-center border-b pb-4">
            <h2 className="text-xl font-bold text-[#f39c12]">Ivory Home Health Care. Inc.</h2>
            <p className="text-xs text-gray-600">Ph: (562) 349 - 0860; fax: (562) 285 – 7216</p>
            <p className="text-xs text-gray-600">Email: office@ivory_hhc.com; ivory_hhc@qbhm.net</p>
            <p className="text-xs text-gray-600">WEBSITE: www.ivory_hhc.com or www.ivory_hhc.net</p>
            <h3 className="text-lg font-bold mt-2">Wound Care Flow Sheet</h3>
          </div>

          {/* Patient Info */}
          <div className="grid grid-cols-2 gap-4 border-b pb-4">
            <div>
              <Label className="font-bold">Patient Name</Label>
              <select
                value={formData.patientId}
                onChange={(e) => {
                  const patient = patients.find(p => p.id === e.target.value);
                  handleInputChange('patientId', e.target.value);
                  if (patient) handleInputChange('patientName', `${patient.firstName} ${patient.lastName}`);
                }}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="">Select Patient...</option>
                {patients.map(p => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </select>
            </div>
            <VoiceDictationInput 
              label="MR:" 
              value={formData.medicalRecordNumber} 
              onChange={(e) => handleInputChange('medicalRecordNumber', e.target.value)} 
            />
          </div>

          {/* Wound Images Section */}
          <div className="border rounded-lg p-4 bg-purple-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-purple-800" />
                <h4 className="font-bold text-lg text-purple-800">Wound Images</h4>
                <span className="text-xs text-gray-500">(Click to enlarge)</span>
              </div>
              <Button 
                onClick={() => woundImageInputRef.current?.click()}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                disabled={uploading}
                size="sm"
                data-testid="upload-wound-image-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Image'}
              </Button>
              <input
                type="file"
                ref={woundImageInputRef}
                onChange={handleWoundImageUpload}
                accept="image/*"
                className="hidden"
                data-testid="wound-image-input"
              />
            </div>

            {(!formData.woundImages || formData.woundImages.length === 0) ? (
              <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center bg-white">
                <Image className="w-10 h-10 mx-auto text-purple-300 mb-2" />
                <p className="text-gray-500 text-sm">No wound images uploaded</p>
                <p className="text-xs text-gray-400">Upload images to track wound progression</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {formData.woundImages.map((image, index) => (
                  <div key={image.id} className="relative group border rounded-lg overflow-hidden bg-white shadow-sm">
                    <img 
                      src={image.imageData} 
                      alt={`Wound ${index + 1}`}
                      className="w-full h-28 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedImage(image)}
                      data-testid={`wound-image-${index}`}
                    />
                    <div className="p-2">
                      <input
                        type="date"
                        value={image.dateTaken}
                        onChange={(e) => updateWoundImage(image.id, 'dateTaken', e.target.value)}
                        className="w-full text-xs border rounded px-2 py-1 mb-1"
                      />
                      <input
                        type="text"
                        value={image.description || ''}
                        onChange={(e) => updateWoundImage(image.id, 'description', e.target.value)}
                        placeholder="Description..."
                        className="w-full text-xs border rounded px-2 py-1"
                      />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeWoundImage(image.id);
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-wound-image-${index}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setSelectedImage(image)}
                      className="absolute top-1 left-1 p-1 bg-white/80 text-gray-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ZoomIn className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Wound Healing Progress Tracker */}
          {formData.woundImages && formData.woundImages.length >= 2 && (
            <WoundHealingTracker 
              woundImages={formData.woundImages}
              woundMeasurements={formData.flowSheetEntries?.map(entry => ({
                date: entry.date,
                width: parseFloat(entry.measurements?.split('x')?.[0]) || 0,
                length: parseFloat(entry.measurements?.split('x')?.[1]) || 0
              })).filter(m => m.width > 0 || m.length > 0)}
            />
          )}

          {/* Wound Etiology */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h4 className="font-bold text-lg mb-3 text-blue-800">Wound Etiology</h4>
            <div className="flex flex-wrap items-center gap-4">
              <CheckboxField label="Trauma" checked={formData.woundEtiologyTrauma} onChange={(v) => handleInputChange('woundEtiologyTrauma', v)} />
              <CheckboxField label="Diabetic" checked={formData.woundEtiologyDiabetic} onChange={(v) => handleInputChange('woundEtiologyDiabetic', v)} />
              <CheckboxField label="Venous Stasis" checked={formData.woundEtiologyVenousStasis} onChange={(v) => handleInputChange('woundEtiologyVenousStasis', v)} />
              <CheckboxField label="Burn" checked={formData.woundEtiologyBurn} onChange={(v) => handleInputChange('woundEtiologyBurn', v)} />
              <CheckboxField label="Pressure" checked={formData.woundEtiologyPressure} onChange={(v) => handleInputChange('woundEtiologyPressure', v)} />
              <CheckboxField label="Surgery" checked={formData.woundEtiologySurgery} onChange={(v) => handleInputChange('woundEtiologySurgery', v)} />
            </div>
            <VoiceDictationInput 
              label="Other:" 
              value={formData.woundEtiologyOther} 
              onChange={(e) => handleInputChange('woundEtiologyOther', e.target.value)} 
              className="mt-2"
            />
            <VoiceDictationInput 
              label="Wound #:" 
              value={formData.woundNumber} 
              onChange={(e) => handleInputChange('woundNumber', e.target.value)} 
              className="mt-2 w-32"
            />
          </div>

          {/* Wound Type */}
          <div className="border rounded-lg p-4 bg-purple-50">
            <h4 className="font-bold text-lg mb-3 text-purple-800">Wound Type</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CheckboxField label="Trauma Wound:" checked={formData.woundTypeTrauma} onChange={(v) => handleInputChange('woundTypeTrauma', v)} />
                <VoiceDictationInput 
                  label="Cause:" 
                  value={formData.woundTypeTraumaCause} 
                  onChange={(e) => handleInputChange('woundTypeTraumaCause', e.target.value)}
                  className="flex-1"
                />
              </div>
              <CheckboxField label="Burn" checked={formData.woundTypeBurn} onChange={(v) => handleInputChange('woundTypeBurn', v)} />
              <div className="flex items-center gap-4">
                <CheckboxField label="Pressure Ulcer Stage:" checked={formData.woundTypePressureUlcer} onChange={(v) => handleInputChange('woundTypePressureUlcer', v)} />
                <CheckboxField label="I" checked={formData.woundTypePressureStageI} onChange={(v) => handleInputChange('woundTypePressureStageI', v)} />
                <CheckboxField label="II" checked={formData.woundTypePressureStageII} onChange={(v) => handleInputChange('woundTypePressureStageII', v)} />
                <CheckboxField label="III" checked={formData.woundTypePressureStageIII} onChange={(v) => handleInputChange('woundTypePressureStageIII', v)} />
                <CheckboxField label="IV" checked={formData.woundTypePressureStageIV} onChange={(v) => handleInputChange('woundTypePressureStageIV', v)} />
              </div>
              <CheckboxField label="Unobservable" checked={formData.woundTypeUnobservable} onChange={(v) => handleInputChange('woundTypeUnobservable', v)} />
              <CheckboxField label="Diabetic Ulcer" checked={formData.woundTypeDiabeticUlcer} onChange={(v) => handleInputChange('woundTypeDiabeticUlcer', v)} />
              <CheckboxField label="Venous Stasis Ulcer" checked={formData.woundTypeVenousStasisUlcer} onChange={(v) => handleInputChange('woundTypeVenousStasisUlcer', v)} />
              <CheckboxField label="Arterial Ulcer" checked={formData.woundTypeArterialUlcer} onChange={(v) => handleInputChange('woundTypeArterialUlcer', v)} />
              <VoiceDictationInput 
                label="Other:" 
                value={formData.woundTypeOther} 
                onChange={(e) => handleInputChange('woundTypeOther', e.target.value)}
              />
            </div>
          </div>

          {/* Wound Type Definitions */}
          <div className="border rounded-lg p-4 bg-gray-50 text-xs">
            <h4 className="font-bold text-sm mb-2">Wound Type Definitions</h4>
            <div className="space-y-1 text-gray-700">
              <p><strong>Trauma:</strong> A wound that resulted from an unintentional injury or accident.</p>
              <p><strong>Burn:</strong> Any injury caused by heat, electricity, chemicals, radiation, or gases.</p>
              <p><strong>Pressure Ulcer:</strong> Any lesion caused by unrelieved pressure resulting in damage of underlying tissue.</p>
              <p><strong>Surgical Wound:</strong> A wound caused by a surgical intervention.</p>
              <p><strong>Diabetic Ulcer:</strong> A chronic wound of the foot that occurs in patients with diabetes.</p>
              <p><strong>Venous Stasis Ulcer:</strong> An ulcer caused by inadequate venous circulation, usually lower legs.</p>
              <p><strong>Arterial Ulcer:</strong> An ulcer caused by inadequate arterial circulation usually located distally.</p>
            </div>
          </div>

          {/* Wound Description */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h4 className="font-bold text-lg mb-3 text-green-800">Wound Description</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <VoiceDictationInput label="Location:" value={formData.woundLocation} onChange={(e) => handleInputChange('woundLocation', e.target.value)} className="col-span-2" />
              <VoiceDictationInput label="Width:" value={formData.woundWidth} onChange={(e) => handleInputChange('woundWidth', e.target.value)} />
              <VoiceDictationInput label="Length:" value={formData.woundLength} onChange={(e) => handleInputChange('woundLength', e.target.value)} />
              <VoiceDictationInput label="Depth:" value={formData.woundDepth} onChange={(e) => handleInputChange('woundDepth', e.target.value)} />
            </div>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <CheckboxField label="Tunneling" checked={formData.hasTunneling} onChange={(v) => handleInputChange('hasTunneling', v)} />
              <CheckboxField label="Undermining" checked={formData.hasUndermining} onChange={(v) => handleInputChange('hasUndermining', v)} />
              <CheckboxField label="Unobservable" checked={formData.woundUnobservable} onChange={(v) => handleInputChange('woundUnobservable', v)} />
            </div>
            <div className="mb-4">
              <Label className="font-semibold">Due to:</Label>
              <div className="flex flex-wrap items-center gap-4 mt-2">
                <CheckboxField label="Eschar" checked={formData.unobservableDueToEschar} onChange={(v) => handleInputChange('unobservableDueToEschar', v)} />
                <CheckboxField label="Cast" checked={formData.unobservableDueToCast} onChange={(v) => handleInputChange('unobservableDueToCast', v)} />
                <CheckboxField label="Non-removable Dressing" checked={formData.unobservableDueToNonRemovableDressing} onChange={(v) => handleInputChange('unobservableDueToNonRemovableDressing', v)} />
                <VoiceDictationInput 
                  label="(type of dressing):" 
                  value={formData.nonRemovableDressingType} 
                  onChange={(e) => handleInputChange('nonRemovableDressingType', e.target.value)}
                  className="w-48"
                />
              </div>
              <VoiceDictationInput 
                label="Other:" 
                value={formData.unobservableDueToOther} 
                onChange={(e) => handleInputChange('unobservableDueToOther', e.target.value)}
                className="mt-2"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <VoiceDictationInput label="Drainage (amount and color):" value={formData.drainageAmountColor} onChange={(e) => handleInputChange('drainageAmountColor', e.target.value)} className="col-span-2" />
              <VoiceDictationInput label="Wound Color:" value={formData.woundColor} onChange={(e) => handleInputChange('woundColor', e.target.value)} />
              <VoiceDictationInput label="Odor:" value={formData.woundOdor} onChange={(e) => handleInputChange('woundOdor', e.target.value)} />
              <VoiceDictationInput label="Surrounding Tissue:" value={formData.surroundingTissue} onChange={(e) => handleInputChange('surroundingTissue', e.target.value)} className="col-span-2" />
            </div>
          </div>

          {/* Granulation Tissue */}
          <div className="border rounded-lg p-4 bg-yellow-50">
            <h4 className="font-bold text-lg mb-3 text-yellow-800">Granulation Tissue</h4>
            <div className="flex flex-wrap items-center gap-4">
              <CheckboxField label="Fully Granulating" checked={formData.fullyGranulating} onChange={(v) => handleInputChange('fullyGranulating', v)} />
              <CheckboxField label="Early / Partial Granulation" checked={formData.earlyPartialGranulation} onChange={(v) => handleInputChange('earlyPartialGranulation', v)} />
              <CheckboxField label="Not Healing" checked={formData.notHealing} onChange={(v) => handleInputChange('notHealing', v)} />
            </div>
          </div>

          {/* Wound Descriptions Definitions */}
          <div className="border rounded-lg p-4 bg-gray-50 text-xs">
            <h4 className="font-bold text-sm mb-2">Wound Descriptions Definitions</h4>
            <div className="space-y-1 text-gray-700">
              <p><strong>Undermining:</strong> Area of tissue destruction extending under intact skin along the periphery of a wound.</p>
              <p><strong>Tunneling:</strong> Course or path of tissue destruction occurring in any direction from the surface or edge of the wound.</p>
              <p><strong>Eschar:</strong> Thick, black or brown leathery, necrotic tissue.</p>
              <p><strong>Slough:</strong> Soft, moist avascular (devitalized) tissue; may be white, yellow, tan, or green.</p>
              <p><strong>Granulation Tissue:</strong> Pink/red, moist tissue comprised of new blood vessels, connective tissue, fibroblast, and inflammatory cells.</p>
              <p><strong>Signs and Symptoms of infection:</strong> Purulent drainage, odor, erythema, warmth, tenderness, edema, pain, fever, and elevated white cell count.</p>
            </div>
          </div>

          {/* Current Treatment */}
          <div className="border rounded-lg p-4 bg-teal-50">
            <h4 className="font-bold text-lg mb-3 text-teal-800">Current Treatment</h4>
            <VoiceDictationInput 
              value={formData.currentTreatment} 
              onChange={(e) => handleInputChange('currentTreatment', e.target.value)}
              rows={4}
            />
          </div>

          {/* Flow Sheet Table */}
          <div className="border rounded-lg p-4 bg-orange-50">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-bold text-lg text-orange-800">Wound Care Flow Sheet Entries</h4>
              <Button onClick={addFlowSheetEntry} size="sm" className="bg-[#f39c12] hover:bg-[#e67e22]">
                <Plus className="w-4 h-4 mr-1" /> Add Entry
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-orange-200">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Wound Location</th>
                    <th className="p-2 text-left">Measurements</th>
                    <th className="p-2 text-left">Drainage Amount</th>
                    <th className="p-2 text-left">Color</th>
                    <th className="p-2 text-left">Odor</th>
                    <th className="p-2 text-left">Comments</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.flowSheetEntries.map((entry, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.date} 
                          onChange={(e) => handleFlowSheetChange(index, 'date', e.target.value)}
                          placeholder="MM/DD/YY"
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.woundLocation} 
                          onChange={(e) => handleFlowSheetChange(index, 'woundLocation', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.measurements} 
                          onChange={(e) => handleFlowSheetChange(index, 'measurements', e.target.value)}
                          placeholder="L x W x D"
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.drainageAmount} 
                          onChange={(e) => handleFlowSheetChange(index, 'drainageAmount', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.drainageColor} 
                          onChange={(e) => handleFlowSheetChange(index, 'drainageColor', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.drainageOdor} 
                          onChange={(e) => handleFlowSheetChange(index, 'drainageOdor', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.comments} 
                          onChange={(e) => handleFlowSheetChange(index, 'comments', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        {formData.flowSheetEntries.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeFlowSheetEntry(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">*Use one wound care flow sheet for each wound.</p>
          </div>

          {/* Signatures - Comprehensive with Type/Voice Recording/Handwritten */}
          <div className="border rounded-lg p-4 bg-gray-100">
            <h4 className="font-bold text-lg mb-3">SIGNATURES</h4>
            <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <SignatureInput 
                  label="Staff Signature" 
                  value={formData.staffSignature} 
                  onChange={(value) => handleInputChange('staffSignature', value)} 
                />
              </div>
              <div className="space-y-3">
                <VoiceDictationInput label="Date" value={formData.staffSignatureDate} onChange={(e) => handleInputChange('staffSignatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Form'}
            </Button>
          </div>
        </div>

        {/* Image Lightbox Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="wound-image-lightbox"
          >
            <div className="relative max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
              <img 
                src={selectedImage.imageData} 
                alt="Wound detail"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="mt-2 text-white text-center">
                <p className="text-sm">Date: {selectedImage.dateTaken}</p>
                {selectedImage.description && <p className="text-sm">Description: {selectedImage.description}</p>}
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-2 -right-2 p-2 bg-white text-gray-800 rounded-full shadow-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WoundCareFlowSheet;
